import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

/**
 * Formats a given date string to the specified format while preserving the UTC context.
 * @param date - The date string to format.
 * @param dateFormat - The desired output format (default: 'MM/dd/yyyy').
 * @param defaultValue - The value to return if the date is invalid (default: '').
 * @returns The formatted date string.
 */
export const formatDate = (date: string, dateFormat = 'MM/dd/yyyy', defaultValue = ''): string => {
	if (!date) return defaultValue;
	try {
		const utcDate = utcToZonedTime(date, 'UTC'); // Ensure date is treated as UTC
		return format(utcDate, dateFormat);
	} catch {
		return defaultValue;
	}
};

/**
 * Formats a given date string to the specified time format while preserving the UTC context.
 * @param date - The date string to format.
 * @param timeFormat - The desired output format (default: 'h:mm a').
 * @returns The formatted time string.
 */
export const formatTime = (date: string, timeFormat = 'h:mm a'): string => {
	if (!date) return '';
	try {
		const utcDate = utcToZonedTime(date, 'UTC'); // Ensure date is treated as UTC
		return format(utcDate, timeFormat);
	} catch {
		return '';
	}
};

/**
 * Converts a given date string to ISO 8601 format while preserving the UTC context.
 * @param dateString - The date string to convert.
 * @returns The formatted ISO 8601 date string.
 */
export const formatToISO8601 = (dateString: string): string => {
	if (!dateString) return '';
	try {
		const utcDate = utcToZonedTime(dateString, 'UTC'); // Ensure date is treated as UTC
		return format(utcDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
	} catch {
		return '';
	}
};

/**
 * Gets the current timezone abbreviation (e.g., EST, PST, etc.).
 * Uses the browser's locale settings to determine the timezone.
 * @returns The timezone abbreviation string.
 */

export function getTimeZoneAbbreviation() {
	const date = new Date();
	const timeString = date.toLocaleTimeString('en-us', { timeZoneName: 'short' });
	const abbreviation = timeString.split(' ').pop();
	return abbreviation;
}
